"use client";

import { useState } from "react";
import { verifyBeforeUpdateEmail } from "firebase/auth";
import { getAuthInstance } from "@/lib/firebase";
import { useTranslations } from "@/i18n/LocaleProvider";

type Stage = "enter" | "sent" | "verified";

/**
 * Optional email verification, via Firebase's own native mechanism -- which is link-based,
 * not a typed code (Firebase has no code-based email OTP primitive; phone gets WhatsApp OTP
 * specifically because WhatsApp has no link-based equivalent, but email has no code-based one,
 * so this is the correct-for-the-platform shape, not an inconsistency with PhoneOtpAuth).
 *
 * `verifyBeforeUpdateEmail` sends the link and hands the actual verification to Firebase's own
 * hosted confirmation page -- this component doesn't need to parse a returning oobCode itself,
 * just reload the current user afterward to see the now-verified email. Requires an existing
 * signed-in user (phone verification happens first in every flow this is used in).
 */
export default function EmailVerificationField({
  email,
  onEmailChange,
  onVerified,
  initiallyVerified = false,
}: {
  email: string;
  onEmailChange: (value: string) => void;
  onVerified: () => void;
  // True when a parent restored a previously-verified email from a saved draft (see
  // draftStorage.ts) -- only read once, at mount.
  initiallyVerified?: boolean;
}) {
  const { t } = useTranslations();
  const [stage, setStage] = useState<Stage>(initiallyVerified ? "verified" : "enter");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function sendLink() {
    const user = getAuthInstance().currentUser;
    if (!email.trim() || !user) return;
    setLoading(true);
    setError(null);
    try {
      await verifyBeforeUpdateEmail(user, email, {
        url: window.location.href,
      });
      setStage("sent");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("emailVerification.sendError"));
    } finally {
      setLoading(false);
    }
  }

  async function confirmVerified() {
    const user = getAuthInstance().currentUser;
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      await user.reload();
      if (user.emailVerified) {
        setStage("verified");
        onVerified();
      } else {
        setError(t("emailVerification.notYetVerified"));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("emailVerification.sendError"));
    } finally {
      setLoading(false);
    }
  }

  if (stage === "verified") {
    return <p className="text-sm text-accent">{t("emailVerification.verified")}</p>;
  }

  return (
    <div className="space-y-3 rounded-lg border border-foreground/10 p-4">
      <input
        type="email"
        placeholder={t("emailVerification.emailPlaceholder")}
        value={email}
        onChange={(e) => onEmailChange(e.target.value)}
        disabled={stage === "sent"}
        className="w-full rounded-lg border border-foreground/20 px-4 py-3 disabled:bg-foreground/5"
      />

      {stage === "enter" && (
        <button
          type="button"
          onClick={() => void sendLink()}
          disabled={loading || !email.trim()}
          className="rounded-xl border border-foreground/20 px-5 py-2 text-sm font-medium disabled:opacity-50"
        >
          {loading ? t("emailVerification.sending") : t("emailVerification.sendLink")}
        </button>
      )}

      {stage === "sent" && (
        <>
          <p className="text-sm text-muted">{t("emailVerification.linkSentNotice")}</p>
          <button
            type="button"
            onClick={() => void confirmVerified()}
            disabled={loading}
            className="rounded-xl border border-foreground/20 px-5 py-2 text-sm font-medium disabled:opacity-50"
          >
            {loading ? t("emailVerification.checking") : t("emailVerification.ivedVerified")}
          </button>
        </>
      )}

      {error && <p className="text-sm text-red-700">{error}</p>}
    </div>
  );
}
